/* One accessible source list; the visual copy fills one seamless loop. */
(() => {
  if (customElements.get('fizzy-benefits')) return;
  customElements.define('fizzy-benefits', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.viewport = this.querySelector('[data-benefits-viewport]');
      this.track = this.querySelector('[data-benefits-track]');
      this.source = this.querySelector('[data-benefits-source]');
        if (!this.viewport || !this.track || !this.source) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.mobile = matchMedia('(max-width: 768px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
      };
      this.mobile.addEventListener('change', this.schedule, options);
      this.reduced.addEventListener('change', this.schedule, options);
      this.touchPointers = new Set();
      this.addEventListener('pointerdown', event => {
        if (event.pointerType !== 'touch') {
          this.classList.remove('is-touch-input');
          return;
        }
        clearTimeout(this.resumeTimer);
        this.touchPointers.add(event.pointerId);
        this.classList.add('is-paused', 'is-touch-input');
      }, options);
      const releaseTouch = event => {
        if (!this.touchPointers.delete(event.pointerId) || this.touchPointers.size) return;
        clearTimeout(this.resumeTimer);
        this.resumeTimer = setTimeout(() => {
          this.resumeTimer = null;
          this.classList.remove('is-paused');
        }, 800);
      };
      // A finger can leave the strip before release; observe the whole window.
      window.addEventListener('pointerup', releaseTouch, options);
      window.addEventListener('pointercancel', releaseTouch, options);
      this.addEventListener('pointermove', event => {
        if (event.pointerType === 'mouse') this.classList.remove('is-touch-input');
      }, options);
      // Visual copies remain mouse-usable links, but never duplicate keyboard stops.
      this.track.addEventListener('mousedown', event => {
        if (event.target.closest('[data-benefits-copy] a')) event.preventDefault();
      }, options);
      this.addEventListener('shopify:block:select', () => this.classList.add('is-editing'), options);
      this.addEventListener('shopify:block:deselect', () => this.classList.remove('is-editing'), options);
      if (typeof ResizeObserver !== 'undefined') {
        this.observer = new ResizeObserver(this.schedule);
        this.observer.observe(this.viewport);
      } else {
        window.addEventListener('resize', this.schedule, { ...options, passive: true });
      }
      this.schedule();
    }
    refresh() {
      if (!this.isConnected) return;
      const mobile = this.mobile.matches;
      const moving = !this.reduced.matches && this.source.children.length > 1 &&
        this.dataset[mobile ? 'motionMobile' : 'motionDesktop'] === 'true';
      const speed = Math.max(10, Math.min(100, Number(this.dataset[mobile ? 'speedMobile' : 'speedDesktop']) || 30));
      this.track.querySelector('[data-benefits-copy]')?.remove();
      this.classList.remove('is-animated');
      this.source.style.minWidth = `${this.viewport.clientWidth}px`;
      if (moving) {
        const distance = this.source.getBoundingClientRect().width;
        if (!distance) return;
        const copy = this.source.cloneNode(true);
        copy.removeAttribute('data-benefits-source');
        copy.setAttribute('data-benefits-copy', '');
        copy.setAttribute('aria-hidden', 'true');
        copy.querySelectorAll('[id],[data-shopify-editor-block],a').forEach(node => {
          node.removeAttribute('id');
          node.removeAttribute('data-shopify-editor-block');
          if (node.matches('a')) node.setAttribute('tabindex', '-1');
        });
        this.track.append(copy);
        this.style.setProperty('--fb-distance', `${distance}px`);
        this.style.setProperty('--fb-duration', `${distance / speed}s`);
        this.classList.add('is-animated');
      }
    }
    disconnectedCallback() {
      this.events?.abort();
      this.observer?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.track?.querySelector('[data-benefits-copy]')?.remove();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = null;
      this.touchPointers?.clear();
      this.classList.remove('is-animated', 'is-paused', 'is-touch-input');
      this.events = null;
    }
  });
})();
